import { InputRule, Node, PasteRule, combineTransactionSteps, findChildrenInRange, getChangedRanges, mergeAttributes } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { Suggestion } from '@tiptap/suggestion';
import emojiRegex from 'emoji-regex';

import { CustomEmojiNodeView } from '../../components/extensions/CustomEmojiNodeView.jsx';
import { ensureEmojiCache, getAllEmojis, getEmojiById, getEmojiByShortcode } from '../emojiCache';

export const EmojiSuggestionPluginKey = new PluginKey('emojiSuggestion');

export const inputRegex = /:([a-zA-Z0-9_+-]+):$/;

export const pasteRegex = /:([a-zA-Z0-9_+-]+):/g;

export const Emoji = Node.create({
  name: 'emoji',

  inline: true,

  group: 'inline',

  selectable: false,

  emojisCollection: false,

  addOptions() {
    return {
      HTMLAttributes: {},
      emojis: [],
      forceFallbackImages: false,
      // Provide a minimal synchronous emoji source backed by a Dexie-powered cache
      emojisCollection: {
        findOne: (filter) => {
          ensureEmojiCache();
          if (!filter) return null;
          if (filter.id) return getEmojiById(filter.id);
          if (filter.shortcode) return getEmojiByShortcode(filter.shortcode);
          return null;
        },
        find: (_filter) => ({
          fetch: () => {
            ensureEmojiCache();
            return getAllEmojis();
          },
        }),
      },
      suggestion: {
        char: ':',
        pluginKey: EmojiSuggestionPluginKey,
        command: ({ editor, range, props }) => {
          // increase range.to by one when the next node is of type "text"
          // and starts with a space character
          const nodeAfter = editor.view.state.selection.$to.nodeAfter;
          const overrideSpace = nodeAfter?.text?.startsWith(' ');
          if (overrideSpace) range.to += 1;

          editor
            .chain()
            .focus()
            .insertContentAt(range, [
              { type: this.name, attrs: props },
              { type: 'text', text: ' ' },
            ])
            .command(({ tr, state }) => {
              tr.setStoredMarks(state.doc.resolve(state.selection.to - 2).marks());
              return true;
            })
            .run();
        },
        allow: ({ state, range }) => {
          const $from = state.doc.resolve(range.from);
          const type = state.schema.nodes[this.name];
          const allow = !!$from.parent.type.contentMatch.matchType(type);

          return allow;
        },
      },
    };
  },

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.dataset.id,
        renderHTML: (attributes) => ({ 'data-id': attributes.id }),
      },
      label: { default: null },
      custom: { default: false },
      image: { default: null },
    };
  },

  // Need this for Edit mode - slurp up the entity and do what we need to return
  // what Richdown needs to render the emoji in the editor,
  // and when reloading the editor when an entity is peristed in it via local storage.
  parseHTML() {
    return [
      {
        tag: `entity[data-type="${this.name}"]`, // Match <entity data-type="emoji">
        getAttrs: (element) => {
          const id = element.getAttribute('data-id');

          // No ID found, can't process this emoji
          if (!id) return false;

          const emojiData = this.options.emojisCollection.findOne({ id });

          if (!emojiData) {
            // Emoji not found in collection, but still parse it
            return {
              id: id,
              custom: false,
              'data-controller': 'tippy',
              'data-tippy-theme': 'mini',
              'data-tippy-content': ':unknown:',
            };
          }

          return {
            id: emojiData.id,
            label: emojiData.shortcode,
            custom: emojiData.kind === 'custom',
            image: emojiData.image_url,
            'data-controller': 'tippy',
            'data-tippy-theme': 'mini',
            'data-tippy-content': `:${emojiData.shortcode}:`,
          };
        },
      },
      // Add a rule to handle <img> tags with data-tippy-content for emojis
      // This is kind of a hack, but for some reason if you select the text of a single big emoji entity,
      // it doesn't get parsed as an emoji node. We don't get the nice <entity> tag.
      // Luckily we have the shortcode in the img, so we can use that.
      // It's a little weird because generally we prefer ID lookups, but this is a special case.
      {
        tag: 'img',
        getAttrs: (element) => {
          const tippyContent = element.getAttribute('data-tippy-content');
          const shortcodeMatch = tippyContent?.match(/^:(.+):$/);
          if (shortcodeMatch) {
            const shortcode = shortcodeMatch[1]; // Extract the content between colons
            const emojiData = this.options.emojisCollection.findOne({ shortcode });

            if (emojiData) {
              return {
                id: emojiData.id,
                label: emojiData.shortcode,
                custom: emojiData.kind === 'custom',
                image: emojiData.image_url,
                'data-controller': 'tippy',
                'data-tippy-theme': 'mini',
                'data-tippy-content': tippyContent,
              };
            }
          }
          return false; // Not an emoji img, let other rules handle it or ignore
        },
      },
    ];
  },
  addNodeView() {
    return ReactNodeViewRenderer(CustomEmojiNodeView);
  },
  renderHTML({ HTMLAttributes, node }) {
    // Look up emoji by ID to get the tooltip content
    const emojiData = node.attrs.id ? this.options.emojisCollection.findOne({ id: node.attrs.id }) : null;

    const attributes = mergeAttributes(
      {
        'data-type': this.name,
        'data-id': node.attrs.id,
        'data-controller': 'tippy',
        'data-tippy-theme': 'mini',
        'data-tippy-content': emojiData ? `:${emojiData.shortcode}:` : ':unknown:',
      },
      this.options.HTMLAttributes,
      HTMLAttributes,
    );

    return ['entity', attributes];
  },

  renderText({ node }) {
    const emojiData = node.attrs.id ? this.options.emojisCollection.findOne({ id: node.attrs.id }) : null;
    return emojiData?.native || (node.attrs.label ? `:${node.attrs.label}:` : ':unknown:');
  },

  addCommands() {
    return {
      insertEmoji:
        (attrs) =>
        ({ commands }) => {
          // Need to get the ID
          const emojiData = this.options.emojisCollection.findOne({ shortcode: attrs.name });

          // attrs should contain id, custom, and optionally label/image
          return commands.insertContent({
            type: this.name,
            attrs: {
              id: emojiData.id,
              label: attrs.name || attrs.label,
              custom: attrs.custom || false,
              image: attrs.image || null,
            },
          });
        },
      setEmoji:
        (shortcode) =>
        ({ chain }) => {
          const emojiData = this.options.emojisCollection.findOne({ shortcode });
          if (!emojiData) return false;

          chain()
            .insertContent({
              type: this.name,
              attrs: {
                id: emojiData.id,
                label: emojiData.shortcode,
                custom: emojiData.kind === 'custom',
                image: emojiData.image_url,
              },
            })
            .command(({ tr, state }) => {
              tr.setStoredMarks(state.doc.resolve(state.selection.to - 1).marks());
              return true;
            })
            .run();

          return true;
        },
    };
  },

  addInputRules() {
    return [
      new InputRule({
        find: inputRegex,
        handler: ({ range, match, chain }) => {
          const shortcode = match[1];
          const emojiData = this.options.emojisCollection.findOne({ shortcode });
          if (!emojiData) return;

          chain()
            .insertContentAt(range, {
              type: this.name,
              attrs: {
                id: emojiData.id,
                label: emojiData.shortcode,
                custom: emojiData.kind === 'custom',
                image: emojiData.image_url,
              },
            })
            .command(({ tr, state }) => {
              tr.setStoredMarks(state.doc.resolve(state.selection.to - 1).marks());
              return true;
            })
            .run();
        },
      }),
    ];
  },

  // This let's us catch paste events for shortcodes, e.g, ":dog:",
  // but only when it's the only thing in the clipboard, as plain text.
  // if there's html this doesn't run.
  addPasteRules() {
    return [
      new PasteRule({
        find: pasteRegex,
        handler: ({ range, match, chain }) => {
          const shortcode = match[1];
          const emojiData = this.options.emojisCollection.findOne({ shortcode });
          if (!emojiData) return;

          chain()
            .insertContentAt(
              range,
              {
                type: this.name,
                attrs: {
                  id: emojiData.id,
                  label: emojiData.shortcode,
                  custom: emojiData.kind === 'custom',
                  image: emojiData.image_url,
                },
              },
              {
                updateSelection: false,
              },
            )
            .command(({ tr, state }) => {
              tr.setStoredMarks(state.doc.resolve(state.selection.to - 1).marks());
              return true;
            })
            .run();
        },
      }),
    ];
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),

      new Plugin({
        key: new PluginKey('emoji'),
        props: {
          // double click to select emoji doesn't work by default
          // that's why we simulate this behavior
          handleDoubleClickOn: (view, pos, node) => {
            if (node.type !== this.type) {
              return false;
            }

            const from = pos;
            const to = from + node.nodeSize;

            this.editor.commands.setTextSelection({
              from,
              to,
            });

            return true;
          },
        },

        // replace text emojis with emoji node on any change
        appendTransaction: (transactions, oldState, newState) => {
          const docChanges = transactions.some((transaction) => transaction.docChanged) && !oldState.doc.eq(newState.doc);
          if (!docChanges) return;

          const { tr } = newState;
          const transform = combineTransactionSteps(oldState.doc, transactions);
          const changes = getChangedRanges(transform);

          changes.forEach(({ newRange }) => {
            // We don't want to add emoji inline nodes within code blocks.
            // Because this would split the code block.

            // This only works if the range of changes is within a code node.
            // For all other cases (e.g. the whole document is set/pasted and the parent of the range is `doc`)
            // it doesn't and we have to double check later.
            if (newState.doc.resolve(newRange.from).parent.type.spec.code) {
              return;
            }

            const textNodes = findChildrenInRange(newState.doc, newRange, (node) => node.type.isText);

            textNodes.forEach(({ node, pos }) => {
              if (!node.text) {
                return;
              }

              const matches = [...node.text.matchAll(emojiRegex())];

              matches.forEach((match) => {
                if (match.index === undefined) {
                  return;
                }

                const emoji = match[0];

                const codePoint = [...emoji]
                  .map((x) => x.codePointAt(0)?.toString(16))
                  .join('-')
                  .toUpperCase();

                const emojis = this.options.emojisCollection.find({}).fetch();
                const matchedEmoji = emojis.find((e) => e.unified == codePoint);

                if (!matchedEmoji) {
                  return;
                }

                const from = tr.mapping.map(pos + match.index);

                // Double check parent node is not a code block.
                if (newState.doc.resolve(from).parent.type.spec.code) {
                  return;
                }

                const to = from + emoji.length;
                const emojiNode = this.type.create({
                  id: matchedEmoji.id,
                });

                tr.replaceRangeWith(from, to, emojiNode);

                tr.setStoredMarks(newState.doc.resolve(from).marks());
              });
            });
          });

          if (!tr.steps.length) return;

          return tr;
        },
      }),
    ];
  },
});
